import { useEffect, useMemo } from 'react';
import { RouteSuggestion } from '@lifeomic/phc-web-toolkit/src/components/Omnibar/types';
import { defineMessages, useIntl } from 'react-intl';
import { messages } from '@lifeomic/phc-web-toolkit/src/components/Omnibar/messages';

import pwtUrls from '@lifeomic/phc-web-toolkit/src/util/urls';
import urls from '../../../util/urls';
import { useOmnibarContext } from '@lifeomic/phc-web-toolkit/src/components/Omnibar/OmnibarProvider';
import {
  useActiveAccount,
  useActiveProjectId,
  useFeatures,
  usePolicy,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import { tiles } from '../../../common/tiles_fl';
import { useSelector } from 'react-redux';
import { featuresSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/features';
import { isFountainLifeAccountId } from '../../../util/fountainLifeAccount';

const localMessages = defineMessages({
  routeDisplay: {
    id: 'omnibar.routes.display.fountainLifeRoute',
    defaultMessage: 'Jump to: {route}',
  },
});

export const useRouteSuggestions = () => {
  const { formatMessage } = useIntl();
  const hasFeature = useFeatures();
  const activeAccount = useActiveAccount();
  const activeProjectId = useActiveProjectId();
  const policy = usePolicy();
  const features = useSelector(featuresSelectors.selectFeatures);
  const fountainLifeUXEnabled =
    hasFeature('fountainLifeUX') || isFountainLifeAccountId(activeAccount?.id);

  const defaultRoutes: RouteSuggestion[] = useMemo(
    () => [
      {
        value: formatMessage(messages.subjectsRoute),
        display: formatMessage(messages.subjectsRouteDisplay),
        path: urls.app.patients(),
        type: 'route',
      },
      {
        value: formatMessage(messages.surveysRoute),
        display: formatMessage(messages.surveysRouteDisplay),
        path: pwtUrls.app.manageSurveys(),
        type: 'route',
      },
      {
        value: formatMessage(messages.insightsRoute),
        display: formatMessage(messages.insightsRouteDisplay),
        path: urls.app.insights(),
        type: 'route',
      },
      {
        value: formatMessage(messages.cohortsRoute),
        display: formatMessage(messages.cohortsRouteDisplay),
        path: urls.app.cohorts(),
        type: 'route',
      },
      {
        value: formatMessage(messages.tasksRoute),
        display: formatMessage(messages.tasksRouteDisplay),
        path: urls.app.tasks(),
        type: 'route',
      },
      {
        value: formatMessage(messages.filesRoute),
        display: formatMessage(messages.filesRouteDisplay),
        path: urls.app.files(),
        type: 'route',
      },
      {
        value: formatMessage(messages.knowledgeRoute),
        display: formatMessage(messages.knowledgeRouteDisplay),
        path: urls.app.projectKnowledge(),
        type: 'route',
      },
      {
        value: formatMessage(messages.addDataRoute),
        display: formatMessage(messages.addDataRouteDisplay),
        path: urls.app.addData(),
        type: 'route',
      },
      {
        value: formatMessage(messages.genomicsRoute),
        display: formatMessage(messages.genomicsRouteDisplay),
        path: urls.app.omics.dashboard(),
        type: 'route',
      },
      {
        value: formatMessage(messages.consentRoute),
        display: formatMessage(messages.consentRouteDisplay),
        path: urls.app.consent(),
        type: 'route',
      },
    ],
    [formatMessage],
  );

  const fountainLifeRoutes: RouteSuggestion[] = useMemo(() => {
    const tileItems = [
      tiles.onboardingSnapshot,
      tiles.fileValidation,
      tiles.regenerativeMedicineOps,
      tiles.reportTemplates,
      tiles.zoomAppointments,
      tiles.aiRules,
      tiles.subjectSearch,
      tiles.manageProjectConsent,
      tiles.manageSurveys,
      tiles.genomics,
    ];

    return tileItems
      .filter(tile => tile.shouldDisplay(activeAccount, policy, features))
      .filter(tile => !tile.requiresProject || activeProjectId)
      .map(tile => ({
        value: tile.title.toLowerCase(),
        display: formatMessage(localMessages.routeDisplay, {
          route: tile.title,
        }),
        path: tile.uri,
        type: 'route' as const,
      }));
  }, [activeAccount, activeProjectId, features, formatMessage, policy]);

  const routes = useMemo(
    () => (fountainLifeUXEnabled ? fountainLifeRoutes : defaultRoutes),
    [defaultRoutes, fountainLifeRoutes, fountainLifeUXEnabled],
  );

  const { searchValue, registerSuggestions, unregisterSuggestions } =
    useOmnibarContext();

  useEffect(() => {
    const filteredRoutes = routes.filter(route =>
      route.value.toLowerCase().includes(searchValue.toLowerCase()),
    );
    registerSuggestions({
      id: 'routeSuggestions',
      loading: false,
      suggestions: filteredRoutes,
    });

    return () => {
      unregisterSuggestions('routeSuggestions');
    };
  }, [registerSuggestions, routes, searchValue, unregisterSuggestions]);
};
